import React from 'react';
import { FormattedMessage } from 'react-intl';

import AvatarCrop from './avatar-crop.jsx';
import AvatarUpload from './avatar-upload.jsx';
import CheckBox from './checkbox.jsx';
import TagManager from './tag-manager.jsx';
import { theCard } from '../lib/utils.js';
import ContactList from '../widgets/contact-list.jsx';
import GroupManager from '../widgets/group-manager.jsx';
import CopyLinkComponent from '../widgets/copy-link-com.jsx';
import Slider from 'react-slick'; // If using react-slick
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import HashNavigation from '../lib/navigation.js';
import { fullFormatter } from '../lib/formatters.js';
import { Drafty } from 'tinode-sdk';


import { AVATAR_SIZE, MAX_AVATAR_BYTES, MAX_EXTERN_ATTACHMENT_SIZE, MAX_TITLE_LENGTH, MAX_PROBLEM_DESCRIPTION_LENGTH,
  MAX_TOPIC_DESCRIPTION_LENGTH } from '../config.js';
import { imageScaled, blobToBase64, makeImageUrl } from '../lib/blob-helpers.js';

const relationshipProblemsMainEN = [
  'I feel like we are not communicating effectively anymore. When I talk about my day, it seems like my partner is distracted and not really listening, which leaves me feeling isolated.',
  'We\'re having financial disagreements lately. I\'m a saver and prefer to have a budget, but my partner is more of a spender, which leads to arguments about our future financial plans',
  'We come from different backgrounds and it\'s challenging to reconcile our values. For instance, I prioritize career growth, while my partner places a greater emphasis on community and social life.',
  'Trust has become a major issue for us. There was a situation where my partner didn\'t tell me about meeting up with an ex, and it\'s been difficult to move past that.',
  'Balancing work and our personal life is a constant struggle. I often have to work late hours, which has led to us spending less quality time together, and it\'s putting a strain on our relationship.'
];

/*
const relationshipProblemsMainRU = [
  'Мне кажется, что мы больше не можем найти общий язык. Когда я рассказываю о прошедшем дне, мой партнер отвлекается и не слушает, и это заставляет меня чувствовать себя одиноким.',
  'В последнее время у нас возникают финансовые разногласия. Я предпочитаю экономить, но мой партнер любит тратить, и это приводит к спорам относительно наших будущих финансовых планов.',
  'У нас с партнером разные взгляды из-за различий в воспитании, и непросто совместить наши ценности. Например, я ставлю карьеру на первое место, а мой партнер больше ценит общественную жизнь и общение.',
  'Доверие стало для нас серьезной проблемой. Была ситуация, когда мой партнер не сказал мне о встрече с бывшим, и мне сложно это оставить в прошлом.',
  'Найти баланс между работой и личной жизнью для нас постоянная борьба. Часто приходится задерживаться на работе, из-за чего мы проводим вместе меньше времени, и это негативно сказывается на наших отношениях.'
];
*/
// Примеры наиболее частых проблем, с которыми люди обращаются к психологу (5 шт.)
// Наиболее частые личные проблемы, с которыми люди обращаются к психологу
const relationshipProblemsMainRU = [
  'Испытываю постоянную тревогу и чувство неуверенности в себе; трудно принимать решения и строить планы.',
  'Частые конфликты с родителями из-за личных границ и желания самостоятельности.',
  'Чувствую эмоциональное выгорание на работе; даже в выходные не удаётся восстановиться.',
  'Сложно справляться с ощущением одиночества и нехваткой близких отношений.',
  'Трудности с управлением гневом: резко реагирую в спорах, а потом испытываю вину.',
  'Мучают навязчивые негативные мысли и проблемы со сном.',
  'Низкая самооценка и сомнения в собственной ценности.',
  'Не могу отпустить прошлые отношения после расставания; испытываю боль и обиду.',
  'Прокрастинация: откладываю важные дела, что влияет на карьеру и уверенность.',
  'Сложно устанавливать и защищать личные границы с друзьями и коллегами.'
];



const employeeProblemsManageEN = [
  'Communication seems to be a recurring issue. We need to ensure that feedback and instructions are clearly understood and acted upon.',
  'We have differing views on project priorities, leading to conflicts and delays. It’s important to align our goals and approaches.',
  'Our working styles vary greatly. While I focus on deadlines and efficiency, you tend to prioritize creativity and flexibility, which sometimes causes friction.',
  'There have been trust issues, especially after you did not inform me about a crucial change. Rebuilding this trust is essential for our collaboration.',
  'Managing workload and meeting deadlines has been problematic. We need to find a way to improve time management and task completion.',
  'Your punctuality and attendance have been inconsistent. This affects not only your work but also the team’s overall performance.',
  'The quality of your recent work has been below expectations. Let’s discuss how we can support you in improving and maintaining high standards.',
  'It seems like you’ve been less motivated and engaged recently. Identifying any issues and finding ways to re-engage you is crucial.',
  'There have been interpersonal conflicts that need to be addressed to maintain a positive work environment.',
  'Your professional growth appears to have stagnated. Let’s talk about your career aspirations and how we can help you achieve them.'
];

const employeeProblemsManageRU = [
  'Проблемы с коммуникацией стали регулярными. Нам нужно убедиться, что обратная связь и инструкции четко понимаются и выполняются.',
  'У нас разные взгляды на приоритеты в проектах, что приводит к конфликтам и задержкам. Важно согласовать наши цели и подходы.',
  'Наши рабочие стили сильно различаются. Я сосредоточен на сроках и эффективности, а ты склонен уделять больше внимания креативности и гибкости, что иногда вызывает напряженность.',
  'Возникли проблемы с доверием, особенно после того, как ты не сообщил мне о важном изменении. Восстановление доверия необходимо для нашего сотрудничества.',
  'Управление рабочей нагрузкой и соблюдение сроков стало проблемой. Нам нужно найти способ улучшить тайм-менеджмент и выполнение задач.',
  'Твоя пунктуальность и посещаемость были непостоянными. Это влияет не только на твою работу, но и на общую производительность команды.',
  'Качество твоей недавней работы ниже ожидаемого. Давай обсудим, как мы можем помочь тебе улучшить и поддерживать высокие стандарты.',
  'Кажется, ты стал менее мотивирован и вовлечен в последние задачи. Выявление проблем и поиск способов вернуть интерес к работе имеют решающее значение.',
  'Возникли межличностные конфликты, которые нужно решить для поддержания положительной рабочей атмосферы.',
  'Твой профессиональный рост, кажется, застопорился. Давай обсудим твои карьерные устремления и как мы можем помочь их достичь.'
];


const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 10000,
  dotsClass: "slick-dots material-dots",
  //nextArrow: <NextArrow />,
  //prevArrow: <PrevArrow />
  arrows: false,
  //centerMode: true, // Enable center mode
  //centerPadding: '30px', // Adjust padding as needed
};

function NextArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
    >
      <i className="material-icons">arrow_forward</i>
    </div>
  );
}

function PrevArrow(props) {
  const { className, style, onClick } = props;
  return (
    <div
      className={className}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
    >
      <i className="material-icons">arrow_back</i>
    </div>
  );
}

export default class NewTopicGroup extends React.PureComponent {
  submitting = false;

  constructor(props) {
    super(props);

    this.methods = [
      { 
        code: '1', 
        names: { 
          en: 'All methods combined', 
          ru: 'Совокупность всех методик' 
        } 
      },
      { 
        code: '2', 
        names: { 
          en: 'Nonviolent Communication: A Language of Life by Marshall B. Rosenberg', 
          ru: '"Ненасильственное общение: Язык жизни" Маршалл Б. Розенберг' 
        } 
      },
      { 
        code: '3', 
        names: { 
          en: 'The Seven Principles for Making Marriage Work by John Gottman', 
          ru: '"7 принципов счастливого брака" Джон Готтман' 
        } 
      },
      { 
        code: '4', 
        names: { 
          en: 'The 5 Love Languages by Gary Chapman', 
          ru: '"5 языков любви" Гэри Чепмен' 
        } 
      },
      { 
        code: '5', 
        names: { 
          en: 'Men Are from Mars, Women Are from Venus by John Gray', 
          ru: '"Мужчины с Марса, Женщины с Венеры" Джон Грей' 
        } 
      },
      { 
        code: '6', 
        names: { 
          en: 'How to Talk So Kids Will Listen & Listen So Kids Will Talk by Adele Faber and Elaine Mazlish', 
          ru: '"Как говорить, чтобы дети слушали, и как слушать, чтобы дети говорили" Адель Фабер и Элейн Мазлиш' 
        } 
      },
      { 
        code: '7', 
        names: { 
          en: 'The set of all methods for a managers', 
          ru: 'Самые эффективные методики для руководителей' 
        } 
      }

      
    ];


    this.moods = [
      { 
        code: '1', 
        names: { 
          en: 'Aggressive', 
          ru: 'Агрессивный' 
        } 
      },
      { 
        code: '2', 
        names: { 
          en: 'Normal', 
          ru: 'Нормальный' 
        } 
      },
      { 
        code: '3', 
        names: { 
          en: 'Friendly', 
          ru: 'Дружелюбный' 
        } 
      } ];

      this.styles = [
        { 
          code: '1', 
          names: { 
              en: 'Formal', 
              ru: 'Формальный' 
          } 
      },
        { 
            code: '2', 
            names: { 
                en: 'Informal', 
                ru: 'Неформальный' 
            } 
        },
        
        { 
            code: '3', 
            names: { 
                en: 'Humorous', 
                ru: 'Юмористический' 
            } 
        },
        { 
            code: '4', 
            names: { 
                en: 'Brutal Frankness (18+)', 
                ru: 'Грубая откровенность (18+)' 
            } 
        },
        { 
            code: '5', 
            names: { 
                en: 'Creative (creative and unusual solutions)', 
                ru: 'Творческий (креативные и необычные решения)' 
            } 
        },
        { 
            code: '6', 
            names: { 
                en: 'Literary (with quotes from books)', 
                ru: 'Литературный (с цитатами из книг)' 
            } 
        },
        { 
            code: '7', 
            names: { 
                en: 'Sympathetic', 
                ru: 'Максимально сочувствующий' 
            } 
        },
        { 
            code: '8', 
            names: { 
                en: 'Youth and slang', 
                ru: 'Молодежный и слэнговый' 
            } 
        },
        { 
            code: '9', 
            names: { 
                en: 'Maximum expert (with professional psychological terms)', 
                ru: 'Максимально экспертный (с профессиональными психологическими терминами)'
            }
        },
        { 
            code: '19', 
            names: { 
                en: 'Pick-up artist techniques for seducing a partner', 
                ru: 'Пикаперские техники  по соблазнению партнера'
            }
        }
        ,
        {
            code: '11', 
            names: { 
                en: 'Gestalt Therapy', 
                ru: 'Гештальт-терапия'
            }
        },
        {
            code: '12', 
            names: { 
                en: 'Cognitive Behavioral Therapy', 
                ru: 'Когнитивно-поведенческая терапия'
            }
        },
        {
            code: '13', 
            names: { 
                en: 'Psychoanalysis', 
                ru: 'Психоанализ'
            }
        },
        {
            code: '14', 
            names: { 
                en: 'Existential Therapy', 
                ru: 'Экзистенциальная терапия'
            }
        },
        {
            code: '15', 
            names: { 
                en: 'Client-Centered Therapy (Rogerian)', 
                ru: 'Клиенто-центрированная терапия (Роджерианская)'
            }
        },
        {
            code: '16', 
            names: { 
                en: 'Transactional Analysis', 
                ru: 'Транзактный анализ'
            }
        },
        {
            code: '18', 
            names: { 
                en: 'Body-Oriented Therapy', 
                ru: 'Телесно-ориентированная терапия'
            }
        }


      
    ];
    

    this.fullName = React.createRef();

    this.state = {
      fullName: '', // full/formatted name
      private: '',
      description: '',
      imageUrl: null,
      tags: [],
      isChannel: false,
      newAvatar: null,
      newAvatarMime: null,
      step: 0,
      topicSelected:null,
      contactList: [],
      selectedContact: null,
      contactsInited: false,
      language: 'en',
      giveAdvice : true,
      isRunningCall: false,
      adviceText: null,
      dialogType: "1", //
      selectedMethod : "1",
      selectedMood : "2",
      selectedStyle : "1",
      relationshipProblemsEN: relationshipProblemsMainEN,
      relationshipProblemsRU: relationshipProblemsMainRU,
      usePro: false,
      showProInfo: false
    };

    this.handleFieldEdit = this.handleFieldEdit.bind(this);
    this.handleImageChanged = this.handleImageChanged.bind(this);
    this.handleAvatarCropped = this.handleAvatarCropped.bind(this);
    this.handleAvatarCropCancel = this.handleAvatarCropCancel.bind(this);
    this.uploadAvatar = this.uploadAvatar.bind(this);
    this.handleTagsChanged = this.handleTagsChanged.bind(this);
    this.handleChannelToggle = this.handleChannelToggle.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleNewGroupSubmit = this.handleNewGroupSubmit.bind(this);
    this.handleMemberSelected = this.handleMemberSelected.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.handleMemberUpdateRequest = this.handleMemberUpdateRequest.bind(this); 
    this.handleClose = this.handleClose.bind(this);
    this.handleThirdStep = this.handleThirdStep.bind(this);
    this.updateTopicName = this.updateTopicName.bind(this);
    this.setBrowserLanguage = this.setBrowserLanguage.bind(this);
    this.handleToggleNeedAdvice = this.handleToggleNeedAdvice.bind(this);
    this.handleSecondStep = this.handleSecondStep.bind(this);
    this.showAdvice = this.showAdvice.bind(this);
    this.getAdvice = this.getAdvice.bind(this);

    this.handleTypeDialogChange = this.handleTypeDialogChange.bind(this);
    this.handlZeroStep = this.handlZeroStep.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleMethodChange = this.handleMethodChange.bind(this);
    this.handleMoodChange = this.handleMoodChange.bind(this);
    this.handleStyleChange = this.handleStyleChange.bind(this);
    this.handleToggleUsePro = this.handleToggleUsePro.bind(this);
    this.toggleProInfo     = this.toggleProInfo.bind(this);
      

  }


  componentDidMount() {

    this.setBrowserLanguage();

    const { params } = HashNavigation.parseUrlHash(window.location.hash); //syy 
    const dialogmethod = (params && params.dialogmethod) || '';

    //console.info("dialogmethod", dialogmethod);
    if (dialogmethod) {
      this.setState({ 
        selectedMethod : String(dialogmethod),
        step: 1,
        dialogType: "3"
        });

        if ( String(dialogmethod) ==  "7" ) {
          this.setState({ 
            relationshipProblemsEN: employeeProblemsManageEN,
            relationshipProblemsRU: employeeProblemsManageRU, });
        } else {
          this.setState({ 
            relationshipProblemsEN: relationshipProblemsMainEN,
            relationshipProblemsRU: relationshipProblemsMainRU, });
    
        }
      }

    if (!this.props.tinode.isAuthenticated()) {

      this.props.eventToGAnalitycs("new_topic_firstd");
      return;
    }





    if (localStorage.getItem("firstDialogType") && localStorage.getItem("firstDialogType") != "") {

      this.setState({dialogType : localStorage.getItem("firstDialogType"),
        fullName: localStorage.getItem("firstDialogName"),
        private: localStorage.getItem("firstDialogText"),
        giveAdvice: localStorage.getItem("firstDialogAdvice") == "true",
        selectedMethod: localStorage.getItem("selectedMethod"),
        selectedMood: localStorage.getItem("selectedMood"),
        selectedStyle: localStorage.getItem("selectedStyle"),
        step:1,
      }); 
    }

    //console.info("this.props.TokensUsed", this.props.TokensUsed);
    //console.info("this.props.Tokens", this.props.Tokens);
    if (this.props.Tokens && this.props.Tokens > 0 && this.props.TokensUsed >= this.props.Tokens ) {
        this.handleClose();
        this.props.onShowAlert(
          this.props.intl.formatMessage({ id: "status.error" }) , // title
          this.props.intl.formatMessage({ id: "tokens.end.content" }) , // content
          (_ => { return this.props.onAddTokens() }),
          this.props.intl.formatMessage({ id: "topup" }), // "OK"
          true, // Show Reject button
          null  // "Cancel"
        );
    }
    if (this.state.dialogType == "2" || this.state.dialogType == "4")
      {
        this.setState({giveAdvice: false});
      } 

    // this.fullName.current.focus();

    this.props.eventToGAnalitycs("new_topic_start");
    
    if (this.props.talkViewOldDialog !== "")
    {
      const topic = this.props.tinode.getTopic(this.props.talkViewOldDialog);

      this.setState({fullName: topic ? this.updateTopicName(topic.public.fn) : ""
          ,private: topic && topic.private ? topic.private.comment : "" });

      
          if ( topic.subscribers) {
            const added = [];  
            topic.subscribers((sub) => {
              added.push(sub);
            }, this);

            //this.props.onMemberUpdateRequest(this.props.topicSelected, added, []);
            //console.info("new group old subscribes initial:", added)
            this.setState({contactList: added});
          }
            
          
    }
  }

  componentDidUpdate(prevProps) {
    // Только если topicSelected изменился
    if (this.props.topicSelected !== prevProps.topicSelected && this.state.step == 7 ) {
        this.getAdvice();
    }
  }

  setBrowserLanguage()  {
    
    const language = this.props.intl.locale.split(/[-_]/)[0];
    this.setState({language: language ? language : 'en'}); 
  };

 updateTopicName(topicName) {
  // Регулярное выражение для поиска числа в скобках в конце строки
  const regex = /\((\d+)\)$/;

  // Проверяем, соответствует ли название топика шаблону
  const match = topicName.match(regex);

  if (match) {
    // Если соответствует, преобразуем число в скобках в число и увеличиваем его на 1
    const num = parseInt(match[1], 10) + 1;
    // Заменяем старое число на новое в названии топика
    return topicName.replace(regex, `(${num})`);
  } else {
    // Если не соответствует, просто добавляем "(2)" к названию топика
    return `${topicName} (2)`;
  }
}

  handleFieldEdit(name, e) {
    this.setState({[name]: e.target.value || ''});
  }

  handleImageChanged(mime, img) {
    this.setState({newAvatar: img, newAvatarMime: mime});
  }

  handleAvatarCropped(mime, blob, width, height) {
    const url = blob ? URL.createObjectURL(blob) : null;
    this.setState({imageUrl: url, newAvatar: null, newAvatarMime: null});
    if (blob) {
      this.uploadAvatar(mime, blob, width, height);
    }
  }

  handleAvatarCropCancel() {
    this.setState({newAvatar: null, newAvatarMime: null});
  }

  handleNewGroupSubmit(name, description, dataUrl, priv, tags, isChannel, dialogType) { //syy



    this.setState({topicSelected: this.props.topicSelected});

    const pro = this.state.usePro ? 1 : 0; 
    
    this.props.onCreateTopic(undefined,
        {public: theCard(name, dataUrl, null, description), private: priv, tags: tags}, isChannel, dialogType,pro);
    
    if (this.state.giveAdvice  && this.state.dialogType != "2"   && this.state.dialogType != "4"  ) { //&& this.state.dialogType != "3" 
       this.showAdvice();
    }  else {
      this.handleSecondStep();
    }

    //this.props.onClose()
  }
  handlZeroStep (choise) {
    this.setState({step: 1 , dialogType : choise});
  }

  handleCancel (){

    
    localStorage.setItem("firstDialogType","" );
    localStorage.setItem("firstDialogName", "" );
    localStorage.setItem("firstDialogText", "" );
    localStorage.setItem("firstDialogAdvice", "" );
    localStorage.setItem("selectedMethod", "" );
    localStorage.setItem("selectedMood", "" );
    localStorage.setItem("selectedStyle", "" );

    if (this.props.tinode.isAuthenticated()) {
      this.props.onClose();
    } else {
      HashNavigation.navigateTo('');
      this.props.onClose();
    }

  }

  handleSecondStep() {


    localStorage.setItem("firstDialogType","" );
    localStorage.setItem("firstDialogName", "" );
    localStorage.setItem("firstDialogText", "" );
    localStorage.setItem("firstDialogAdvice", "" );
    localStorage.setItem("selectedMethod", "" );
    localStorage.setItem("selectedMood", "" );
    localStorage.setItem("selectedStyle", "" );
    
    if (this.props.searchableContacts.length > 0  && this.state.dialogType == 1) {
      this.setState({step: 2});
      if (this.state.dialogType == 2 || this.state.dialogType == 4 ) {
        this.props.eventToGAnalitycs("new_topic_s2_psy");
      } else {
        this.props.eventToGAnalitycs("new_topic_s2");
      }
    } else {
      this.handleThirdStep();
    }
    
  }

  handleContextMenu(params) {
    const {formatMessage} = this.props.intl;
    const topic = this.props.tinode.getTopic(this.props.topicSelected);
    if (!topic) {
      return;
    }
    const user = topic.subscriber(params.topicName);
    if (!user || !user.acs) {
      return;
    }

    const isMe = this.props.tinode.isMe(params.topicName);
    const menuItems = [{
      title: formatMessage(messages.edit_permissions),
      handler: _ => this.handleLaunchPermissionsEditor(isMe ? 'want' : 'user', params.topicName)
    }];
    if (!isMe) {
      menuItems.push('member_delete');
    }
    menuItems.push(user.acs.isMuted() ? 'member_unmute' : 'member_mute');
    if (!isMe) {
      menuItems.push(user.acs.isJoiner() ? 'member_block' : 'member_unblock');
    }
    this.props.showContextMenu({
      topicName: this.props.topic,
      x: params.x,
      y: params.y,
      user: params.topicName}, menuItems);
  }

  // Utility method for converting cropped avatar blob to bytes for sending inband or
  // for uploading it to the server out of band.
  uploadAvatar(mime, blob, width, height) {
    const readyToUpload = image => {
      let {mime, blob} = image;
      if (blob.size > MAX_AVATAR_BYTES) {
        // Too large to send inband - uploading out of band and sending as a link.
        const uploader = this.props.tinode.getLargeFileHelper();
        uploader.upload(blob)
          .then(url => this.setState({imageUrl: url}))
          .catch(err => this.props.onError(err.message, 'err'));
      } else {
        // Convert blob to base64-encoded bits.
        blobToBase64(blob)
          .then(b64 => this.setState({imageUrl: makeImageUrl({data: b64.bits, type: mime})}));
      }
    };

    if (width > AVATAR_SIZE || height > AVATAR_SIZE || width != height) {
      // Avatar is not square or too large even after cropping. Shrink it and make square.
      imageScaled(blob, AVATAR_SIZE, AVATAR_SIZE, MAX_EXTERN_ATTACHMENT_SIZE, true)
        .then(scaled => readyToUpload(scaled))
        .catch(err => this.props.onError(err.message, 'err'));
    } else {
      readyToUpload({mime: mime, blob: blob, width: width, height: height});
    }
  }

  handleTagsChanged(tags) {
    this.setState({tags: tags});
  }

  handleChannelToggle() {
    this.setState({isChannel: !this.state.isChannel});
  }

  handleMemberSelected(uid) {
    this.setState({selectedContact: uid});
  }

  handleSubmit (e) {
    e.preventDefault();

    if (!this.props.tinode.isAuthenticated()) {
      localStorage.setItem("firstDialogType", this.state.dialogType );
      localStorage.setItem("firstDialogName", this.state.fullName );
      localStorage.setItem("firstDialogText", this.state.private );
      localStorage.setItem("firstDialogAdvice", this.state.giveAdvice );
      localStorage.setItem("selectedMethod", this.state.selectedMethod );
      localStorage.setItem("selectedMood", this.state.selectedMood );
      localStorage.setItem("selectedStyle", this.state.selectedStyle );

      
      HashNavigation.navigateTo('');
      this.props.onClose();
      return;
    }

    const fn = this.state.fullName.trim().substring(0, MAX_TITLE_LENGTH);
    let comment = this.state.private.trim().substring(0, MAX_PROBLEM_DESCRIPTION_LENGTH);

    if (this.state.dialogType == "3" ) {
      const selectedMethod = this.methods.find(m => m.code === this.state.selectedMethod);
      comment = selectedMethod ?  comment + " \n\n" + this.props.intl.formatMessage({ id: "choosed.method" }) + selectedMethod.names[this.state.language] : '';
      
      const selectedMood = this.moods.find(m => m.code === this.state.selectedMood);
      comment = selectedMood ?  comment + " \n\n" + this.props.intl.formatMessage({ id: "choosed.mood" }) + selectedMood.names[this.state.language] : '';

    }

    if (this.state.dialogType != "2"  && this.state.dialogType != "4") {
      const selectedStyle = this.styles.find(m => m.code === this.state.selectedStyle);
      comment = selectedStyle ?  comment + " \n\n" + this.props.intl.formatMessage({ id: "choosed.style"}) + selectedStyle.names[this.state.language] : '';
    }

    const description = ''; //this.state.description.trim().substring(0, MAX_TOPIC_DESCRIPTION_LENGTH);
    if (fn && comment && this.state.step != 2) {
      if (this.submitting) {
        return;
      }
      this.submitting = true;

      try {
        
        this.handleNewGroupSubmit(fn, description, this.state.imageUrl, comment, this.state.tags, this.state.isChannel, this.state.dialogType);
      
      } finally {
        // 3. Снимаем блокировку
        this.submitting = false;
      }
    }
  }

  handleClose(){
    localStorage.setItem("firstDialogType","" );
    localStorage.setItem("firstDialogName", "" );
    localStorage.setItem("firstDialogText", "" );
    localStorage.setItem("firstDialogAdvice", "" );
    localStorage.setItem("selectedMethod", "" );
    localStorage.setItem("selectedMood", "" );
    localStorage.setItem("selectedStyle", "" );
    

    if (this.props.tinode.isAuthenticated()) {
      this.props.onClose();
    } else {
      HashNavigation.navigateTo('');
      this.props.onClose();
    }
    
  }

  handleThirdStep(){

    if (this.props.isNeedTest && this.state.dialogType == 1) {
      this.props.eventToGAnalitycs("new_topic_s3");
      this.setState({step: 3});
    } else {
      this.props.onClose();
      //handleClose();
    }
  }

  showAdvice() {
    this.setState({step: 7});
    this.props.eventToGAnalitycs("new_topic_s7_advice");
    //this.getAdvice();
  }


  async getAdvice  () {
       

    this.setState({isRunningCall: true});
    
    const topic = this.props.tinode.getTopic(this.props.topicSelected );
    const query = topic.startMetaQuery().withDesc().build();

    query.desc.inchatgpttype = "getAdvice";
    //query.desc.inchatgptlang = language; 

    if (this.state.dialogType == 2 || this.state.dialogType == 4 ) {
      query.desc.inchatgpttype = "getAdvice2";
    } else  if (this.state.dialogType == 3 ) {
      query.desc.inchatgpttype = "getAdvice3";
    }

    query.desc.inchatgptlang = this.state.language; 
    
    try {

      const result = await topic.getMeta(query);


      
      if (!result || result.desc.outchatgpt === "") {
        //setError(intl.formatMessage({ id: "error.chat.general" }) + " (1005)");
        this.setState({isRunningCall: false, adviceText: 'Error'});
      } else {
        const text = result.desc.outchatgpt ;
        const parsedObject = JSON.parse(text);

        const tree = Drafty.format(parsedObject, fullFormatter, {
          formatMessage: this.props.intl.formatMessage.bind(this.props.intl),
          viewportWidth: null,
          authorizeURL: this.props.tinode.authorizeURL.bind(this.props.tinode),
          onImagePreview: null,
          onVideoPreview: null,
          onFormButtonClick: null,
          onQuoteClick: null
        });
        const advice = React.createElement(React.Fragment, null, tree);

  
        this.setState({isRunningCall: false, adviceText: advice});
        //console.log('Success:', result);
      }
    } catch (error) {
      console.error('Error:', error);
      //setError(intl.formatMessage({ id: "error.chat.general" }) + " (1004)");
      this.setState({isRunningCall: false});
    } finally {
      
      this.setState({isRunningCall: false});
    }
  }

  handleMemberUpdateRequest(members, added, removed) {
    this.props.onMemberUpdateRequest(this.props.topicSelected, added, removed);
    //this.props.onClose();
    this.handleThirdStep();
    //this.props.onNavigate('info');
  }

  handleToggleNeedAdvice(ignored, checked) {
    
    this.setState({giveAdvice: checked});
  }

  handleTypeDialogChange = (e) => {
    this.setState({ dialogType: e.target.value });

    if ( e.target.value  == "3" && this.state.selectedMethod ==  "7" ) {
      this.setState({ 
        relationshipProblemsEN: employeeProblemsManageEN,
        relationshipProblemsRU: employeeProblemsManageRU, });
    } else {
      this.setState({ 
        relationshipProblemsEN: relationshipProblemsMainEN,
        relationshipProblemsRU: relationshipProblemsMainRU, });

    }

  };

  handleMethodChange = (event) => {
    this.setState({ selectedMethod: event.target.value });

    if ( event.target.value == "7" && this.state.dialogType ==  "3" ) {
      this.setState({ 
        relationshipProblemsEN: employeeProblemsManageEN,
        relationshipProblemsRU: employeeProblemsManageRU, });
    } else {
      this.setState({ 
        relationshipProblemsEN: relationshipProblemsMainEN,
        relationshipProblemsRU: relationshipProblemsMainRU, });

    }
  }


  handleMoodChange = (event) => {
    this.setState({ selectedMood: event.target.value });
  }

  handleStyleChange = (event) => {
    this.setState({ selectedStyle: event.target.value });
  }

  handleToggleUsePro(ignored, checked) {
    this.setState({ usePro: checked });
  }
  
  toggleProInfo() {
    this.setState(prev => ({ showProInfo: !prev.showProInfo }));
  }
  

  render() {
    if (this.state.newAvatar) {
      return (
        <AvatarCrop
          avatar={this.state.newAvatar}
          mime={this.state.newAvatarMime}
          onSubmit={this.handleAvatarCropped}
          onCancel={this.handleAvatarCropCancel}
          onError={this.props.onError} />
      );
    }

    let submitClasses = 'primary';
    if (this.props.disabled) {
      submitClasses += ' disabled';
    }
    if (this.state.step == 0  ) {
      return (
        <div className="new-talk-overlay">
        <div className="new-talk-container">

            <div className="cancel">
                <a href="#" onClick={e => {e.preventDefault(); this.handleCancel ();}}><i className="material-icons gray">close</i></a>
            </div>

            <div className="typedialog typedialog-psychologist">
                <div className="topblock">
                    <div > 
                        <img src="/img/img2c-premium.png" alt=""  className="img" />
                    </div>
                    <dvi className="content">
                        <h1 ><FormattedMessage id="dialogtype2.title" defaultMessage="Dialogue with a virtual psychologist" description="Dialogue with a virtual psychologist" />  </h1>
                        <p className="contenttext"> 
                          <FormattedMessage id="dialogtype2.text" defaultMessage="Choose this option if you need advice or want to discuss your problem with a virtual psychologist who can help you find a solution to the problem" description="Choose this option if you need advice or want to discuss your problem with a virtual psychologist who can help you find a solution to the problem" />
                        </p>
                </dvi>
                </div>  
                <div className='botblock'>
                    <button className='primary'  onClick={(e) => { e.preventDefault(); this.handlZeroStep("4"); }}> 
                      <FormattedMessage id="dialogtype.button" defaultMessage="Choose" description="Choose" /> </button>
                </div>
            </div>

            <div className="typedialog typedialog-partner">
                <div className="topblock">
                    <div > 
                        <img src="/img/img1c-premium.png" alt=""  className="img" />
                    </div>
                    <dvi className="content">
                        <h1 ><FormattedMessage id="dialogtype1.title" defaultMessage="Dialogue with a partner" description="Dialogue with a partner" />  </h1>
                        <p className="contenttext"> 
                          <FormattedMessage id="dialogtype1.text" defaultMessage="Select this option if you want to discuss the problem with your partner. ChatGPT will help you formulate effective and clear messages" description="Select this option if you want to discuss the problem with your partner. ChatGPT will help you formulate effective and clear messages" />
                        </p>
                </dvi>
                </div>  
                <div className='botblock'>
                    <button className='primary' onClick={(e) => { e.preventDefault(); this.handlZeroStep("1"); }}>
                      <FormattedMessage id="dialogtype.button" defaultMessage="Choose" description="Choose" /> </button>
                </div>
            </div>




            <div className="typedialog typedialog-practice">
                <div className="topblock">
                    <div > 
                        <img src="/img/img3c-premium.png" alt=""  className="img" />
                    </div>
                    <dvi className="content">
                        <h1 ><FormattedMessage id="dialogtype3.title" defaultMessage="Dialogue with a virtual psychologist" description="Dialogue with a virtual psychologist" />  </h1>
                        <p className="contenttext"> 
                          <FormattedMessage id="dialogtype3.text" defaultMessage="Choose this option if you need advice or want to discuss your problem with a virtual psychologist who can help you find a solution to the problem" description="Choose this option if you need advice or want to discuss your problem with a virtual psychologist who can help you find a solution to the problem" />
                        </p>
                        {this.state.language == "ru" ?
                        <a href="/books-ru.html" target="_blank">
                            <FormattedMessage id="methods_about" defaultMessage="More about methods"
                                        description="More about methods" /> 
                        </a> :
                        <a href="/books-en.html" target="_blank">
                        <FormattedMessage id="methods_about" defaultMessage="More about methods"
                                    description="More about methods" /> 
                        </a>}

                </dvi>
                </div>  
                <div className='botblock'>
                    <button className='primary'  onClick={(e) => { e.preventDefault(); this.handlZeroStep("3"); }}>
                      <FormattedMessage id="dialogtype.button" defaultMessage="Choose" description="Choose" /> </button>
                </div>
            </div>

        </div>
      </div>
      )

    } else if (this.state.step == 7  ) {
      return (
        <div className="new-talk-overlay">
          <div className="new-talk-container">


            <div>
                <h3> <FormattedMessage id="advice_title" defaultMessage="Tips for solving" description="Tips for solving" /> </h3>
            </div>

            <div className="card">
              {this.state.isRunningCall || this.state.adviceText === null || this.state.adviceText === ''  ? <div>  <FormattedMessage id="loading" defaultMessage="Loading..." description="Loading..." /> </div> :
              this.state.adviceText
              /*{<div  dangerouslySetInnerHTML={{ __html: this.state.adviceText.replace(/\n/g, '<br />') }} /> }*/
              
              /*this.state.adviceText*/
              }

            </div>

            <div className="dialog-buttons">
              <button className='primary' onClick={(e) => { e.preventDefault(); this.handleSecondStep(); }}>
                <FormattedMessage id="button_next" defaultMessage="Next"
                  description="Button [Next]" />
              </button>

            </div>

          </div>

        </div>)
    }
    else if (this.state.step == 3) {
      return (
        <div className="new-talk-overlay">
          
          <div className="new-talk-container">
              <div>
                <h3> <FormattedMessage id="sidepanel_title_acc_test" defaultMessage="Test" description="Test" /> </h3>
              </div>
              <div>
              <FormattedMessage id="newtopic_dotest_desc" defaultMessage="For improved results, it's advisable to take the Psychological Test. It consists of just 5 questions."
                    description="Caption [DoTest]" />

               
              </div>

              <div className="dialog-buttons">
                <button className='primary' onClick={(e) => { e.preventDefault(); this.props.onClose(); this.props.showTestView();  }}>
                  <FormattedMessage id="button_dotest" defaultMessage="Go to the test"
                    description="Button [Test]" />
                </button>

                <button className="secondary" onClick={() => this.props.onClose()}>
                  <FormattedMessage id="button_skip" defaultMessage="Skip"
                    description="Button [Skip]" />
                </button>
            </div>

          </div>
        </div>
      );
    } else  if  (this.state.step == 2 && this.props.topicSelected !== this.state.topicSelected)
    {
      //this.state.fullName
      const newtopic = this.props.tinode.getTopic(this.props.topicSelected );
      /*
      console.info("newtopic:");
      console.info(newtopic);
      console.info("this.state.fullName:");
      console.info(this.state.fullName);

      */
    
      if (newtopic.public && (newtopic.public.fn == this.state.fullName))
      {
 
        if (this.props.talkViewOldDialog !== "" &&  !this.state.contactsInited)
        {
          const oldtopic = this.props.tinode.getTopic(this.props.talkViewOldDialog);
  /*
          const added = [];  
          
          oldtopic.subscribers((sub) => {
            
            added.push(sub);
          }, this);
    
          console.info("new group old subscribes:", added)
          this.setState({contactList: added, contactsInited: true});
          */

          const added = [];  
          for (const sub of this.state.contactList) {
            added.push(sub.user);
          }

          //console.info("new group old subscribes added:", added)
          this.setState({contactsInited: true});
          this.props.onMemberUpdateRequest(this.props.topicSelected, added, []);
              
        }
        
        return (
          <div className="new-talk-overlay">
            
            <div className="new-talk-container">
                <div>
                  <h3> <FormattedMessage id="newtopic_adduser" defaultMessage="Add user"
                    description="Add user" /> </h3>
                </div>

                <div className="card">

                  
                  <div >
                  <FormattedMessage id="newtopic_linkdesc" defaultMessage="Your partner can join by this link, copy and send to your partner:"
                      description="Your partner can join by this link, copy and send to your partner:" />

                    
                </div>
                <div>{/*
                  <a href>
        {`${window.location.protocol}//${window.location.hostname}/#?join=${newtopic.name}`} </a>*/}

                    <CopyLinkComponent topicName={newtopic.name} />
                </div>

               </div>
                <div className="new-talk-container-text">
                <FormattedMessage id="newtopic_adduserman" defaultMessage="Or you can add mannualy from your current contact list:"
                    description="Or you can add mannualy from your current contact list:" />
                  
                </div>
                <div className="card">
                  <GroupManager
                      tinode={this.props.tinode}
                      members={this.state.contactList}
                      requiredMember={this.props.myUserId}
                      keepInitialMembers={false}
                      myUserId={this.props.myUserId}
                      contacts={this.props.searchableContacts}
                      onCancel={this.handleThirdStep}
                      onSubmit={this.handleMemberUpdateRequest} />
                  </div>

              {/*
                <FormattedMessage id="group_has_no_members" defaultMessage="No members"
                  description="Shown in place of group members">{
                  (no_members) => <ContactList
                    tinode={this.props.tinode}
                    contacts={this.state.contactList}
                    myUserId={this.props.myUserId}
                    emptyListMessage={no_members}
                    topicSelected={this.state.selectedContact}
                    showOnline={false}
                    showUnread={false}
                    showMode={true}
                    noScroll={true}
                    onTopicSelected={this.handleMemberSelected}
                    showContextMenu={this.handleContextMenu}
                  />
                }</FormattedMessage>
              */}

            </div>
          </div>
        );
      }
    }

    return (
      <div className="new-talk-overlay">
        <div className="new-talk-container">
          <form className="panel-form" onSubmit={this.handleSubmit}>
            <div className="panel-form-column">
             
              <h2 className='center-text'>
              <FormattedMessage id="tabtitle_new_group" defaultMessage="New Talk"
                    description="New Talk'" /> 
              </h2>

              <div className="margin-bottom-small margin-top-small">
                <label className="test-option">
                  <input
                    type="radio"
                    name="gender"
                    value="4"
                    checked={this.state.dialogType == 4}
                    onChange={this.handleTypeDialogChange}
                  />
                  <FormattedMessage id="dialog.psy" defaultMessage="Сonversation with a virtual psychologist" />
                </label>

                <label className="test-option">
                  <input
                    type="radio"
                    name="gender"
                    value="1"
                    checked={this.state.dialogType == 1}
                    onChange={this.handleTypeDialogChange}
                  />
                  <FormattedMessage id="dialog.person" defaultMessage="Сonversation with a partner" />
                </label>



                <label className="test-option">
                  <input
                    type="radio"
                    name="gender"
                    value="3"
                    checked={this.state.dialogType == 3}
                    onChange={this.handleTypeDialogChange}
                  />
                  <FormattedMessage id="dialog.psytr" defaultMessage="Practice negotiations with a virtual psychologist" />
                </label>
                
              </div>


              <div className="group">
                <label className="small" htmlFor="new-topic-fn">
                  <FormattedMessage id="label_topic_name" defaultMessage="Name"
                    description="Label for editing topic name" />*
                </label>
                <FormattedMessage id="topic_name_editing_placeholder" defaultMessage="Freeform name of the talk"
                  description="Prompt for entering talk name">{
                  (placeholder) => <input type="text" id="new-topic-fn" placeholder={placeholder}
                    ref={this.fullName} value={this.state.fullName} onChange={this.handleFieldEdit.bind(this, 'fullName')}
                    autoFocus required tabIndex={0} />
                }</FormattedMessage>
              </div>
              <div className="group">
                <label className="small" htmlFor="new-topic-priv">
                  <FormattedMessage id="label_private" defaultMessage="Private comment"
                    description="Label for editing 'private'" />*
                </label>
                <FormattedMessage id="private_editing_placeholder" defaultMessage="Visible to you only"
                  description="Placeholder for editing 'private'">{
                  (placeholder) => <textarea  id="new-topic-priv" placeholder={placeholder} rows="7" maxLength={ MAX_PROBLEM_DESCRIPTION_LENGTH}
                    value={this.state.private} onChange={this.handleFieldEdit.bind(this, 'private')} required tabIndex={1} />
                }</FormattedMessage>
              </div>
              
              {/*this.state.dialogType == "4" && (
                  <div className="group pro-option">
                    <label className="pro-checkbox">
                      <CheckBox
                        name="usePro"
                        checked={this.state.usePro}
                        onChange={this.handleToggleUsePro}
                      />&nbsp;
                       
                      <span
                        dangerouslySetInnerHTML={{
                          __html: this.props.intl.formatMessage({ id: "new_topic_use_pro" })
                        }}
                      />
                      &nbsp;
                      <i
                        className="material-icons gray pointer"
                        onClick={this.toggleProInfo}
                        aria-label="info"
                      >
                        info_outline
                      </i>
                    </label>

                    {this.state.showProInfo && (
                      <div className="pro-info help-text">
                        <FormattedMessage id="new_topic_pro_info" />
                      </div>
                    )}
                  </div>
                )*/}

              {this.state.dialogType != "2"  && this.state.dialogType != "4" &&
                <div className="group">
                  <div className="advice-check">
                    <CheckBox name="P" checked={this.state.giveAdvice}
                      onChange={this.handleToggleNeedAdvice} /> &nbsp;
                    <FormattedMessage id="new_topic_need_advice" defaultMessage="Need advice on a problem"
                      description="Need advice on a problem"> </FormattedMessage>
                  </div>
                </div>
              }

              {this.state.dialogType == "3" &&
              <>
                <div className="group">
                    <label className="small" htmlFor="new-topic-method">
                    <FormattedMessage id="label_method" defaultMessage="Negotiation technique"
                      description="Negotiation technique" />
                    </label>

                    <div className="method-selector" id="new-topic-method">
                    <select
                      value={this.state.selectedMethod}
                      onChange={this.handleMethodChange}
                      
                    >
                      {this.methods.map(m => (
                        <option key={m.code} value={m.code}>
                        {m.names[this.state.language]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='methods-books'>
                    {this.state.language == "ru" ?
                          <a href="/books-ru.html" target="_blank">
                              <FormattedMessage id="methods_about" defaultMessage="More about methods"
                                          description="More about methods" /> 
                          </a> :
                          <a href="/books-en.html" target="_blank">
                          <FormattedMessage id="methods_about" defaultMessage="More about methods"
                                      description="More about methods" /> 
                          </a>}
                  </div>

                </div>
                
                <div className="group">
                    <label className="small" htmlFor="new-topic-method2">
                    <FormattedMessage id="label_mood" defaultMessage="Goodwill of the companion"
                      description="Goodwill of the companion" />
                    </label>

                    <div className="method-selector" id="new-topic-method2">
                    <select
                      value={this.state.selectedMood}
                      onChange={this.handleMoodChange}
                      
                    >
                      {this.moods.map(m => (
                        <option key={m.code} value={m.code}>
                        {m.names[this.state.language]}
                        </option>
                      ))}
                    </select>
                  </div>


                </div>



                </>}

            {this.state.dialogType != "2" && this.state.dialogType != "4" &&
                <div className="group">
                    <label className="small" htmlFor="new-topic-method3">
                    <FormattedMessage id="label_style" defaultMessage="Style of messages and advice"
                      description="Style of messages and advice" />
                    </label>

                    <div className="method-selector" id="new-topic-method3">
                    <select
                      value={this.state.selectedStyle}
                      onChange={this.handleStyleChange}
                      
                    >
                      {this.styles.map(m => (
                        <option key={m.code} value={m.code}>
                        {m.names[this.state.language]}
                        </option>
                      ))}
                    </select>
                  </div>


                </div>
            }

          </div>
            <div className="dialog-buttons">
              <button className={submitClasses} disabled={this.submitting}>
                <FormattedMessage id="button_next" defaultMessage="Next"
                  description="Button [Next]" />
              </button>

              <button className="secondary" onClick={ () => this.handleCancel()}>
                <FormattedMessage id="button_cancel" defaultMessage="Cancel"
                  description="Button [Cancel]" />
              </button>
            </div>
            <div className="margin-top-small">
              <FormattedMessage id="newtopic_examples" defaultMessage="Examples"
                    description="Examples" />
            </div>
            { this.state.relationshipProblemsEN &&
            <div className="problem-slider" >
              <Slider {...sliderSettings}>
                {this.state.relationshipProblemsEN.map((problem, index) => (
                  <div key={index} className="problem-slide">
                    <div className="problem-content">
                      <p>{ this.state.language == 'ru' ? this.state.relationshipProblemsRU[index] :  this.state.relationshipProblemsEN[index] }</p>
                    </div>
                  </div>
                ))}
              </Slider>
                </div>
            }

            
          </form>
        </div>
      </div>
    );
  }
};
